import { AppIcon } from '@/components/ui/app-icon';
import { useUser } from '@clerk/react';
import { useFeatureFlagEnabled, usePostHog } from 'posthog-js/react';
import { Dialog as DialogPrimitive } from 'radix-ui';
import { useState } from 'react';

import { PRIMARY_BUTTON } from '@/components/ui/button';
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from '@/components/ui/dialog';
import { useFeatureAccess } from '@/hooks/use-feature-access.hook';
import { bestEffortAnalytics } from '@/lib/best-effort-analytics';
import { CARD_TRIAL_DAYS, CARD_TRIAL_FLAG } from '@/lib/card-trial';
import { iconBolt } from '@/lib/icons';
import { cn } from '@/lib/utils';

import { TrialActivateGate } from './trial-activate-gate.component';

/**
 * Offers the trial again to a Free org whose offer is still open
 * (`card_required`), e.g. after "Continue on Free" in onboarding, and opens
 * the same offer screen. Not dismissible. Behind the card-trial flag, read
 * fail-closed.
 */
export function TrialOfferBanner() {
  const posthog = usePostHog();
  const { trial } = useFeatureAccess();
  const { user } = useUser();
  const enabled = useFeatureFlagEnabled(CARD_TRIAL_FLAG) === true;
  const [open, setOpen] = useState(false);

  if (
    !enabled ||
    !trial?.card_required ||
    user?.unsafeMetadata?.completed_onboarding !== true
  ) {
    return null;
  }

  const days = trial.days_remaining || CARD_TRIAL_DAYS;

  const openOffer = () => {
    setOpen(true);
    bestEffortAnalytics(() =>
      posthog?.capture('card_trial_offer_banner_clicked')
    );
  };

  return (
    <>
      <div
        role='region'
        aria-label='Free trial offer'
        className={cn(
          'flex w-full flex-wrap items-center justify-center gap-x-4 gap-y-2',
          'border-b border-border bg-card px-4 py-2 text-[13px] sm:flex-nowrap',
          'bg-[linear-gradient(90deg,hsl(var(--primary)/0.05)_0%,transparent_45%,hsl(var(--primary)/0.05)_100%)]',
          'dark:bg-[linear-gradient(90deg,hsl(var(--primary)/0.11)_0%,transparent_45%,hsl(var(--primary)/0.11)_100%)]'
        )}
      >
        <span className='flex min-w-0 items-center gap-2.5'>
          <AppIcon
            icon={iconBolt}
            className='hidden h-3 w-3 shrink-0 text-primary sm:inline-block'
          />
          <span className='min-w-0 text-center sm:truncate sm:text-left'>
            <span className='font-semibold text-foreground'>
              Start your free {days}-day{' '}
              {trial.plan === 'pro' ? 'Pro' : 'Enterprise'} trial
            </span>
          </span>
        </span>

        <button
          type='button'
          onClick={openOffer}
          className={cn(
            PRIMARY_BUTTON,
            'inline-flex h-7 shrink-0 items-center gap-1.5 px-3 text-xs font-semibold whitespace-nowrap',
            'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:outline-hidden'
          )}
        >
          Try {trial.plan === 'pro' ? 'Pro' : 'Enterprise'} free
        </button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogPortal>
          <DialogOverlay />
          <DialogPrimitive.Content
            aria-describedby={undefined}
            className='fixed inset-0 z-50 overflow-y-auto bg-background focus:outline-hidden data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0'
          >
            <DialogTitle className='sr-only'>
              Your free {trial.plan === 'pro' ? 'Pro' : 'Enterprise'} trial
            </DialogTitle>
            <TrialActivateGate onClose={() => setOpen(false)} />
          </DialogPrimitive.Content>
        </DialogPortal>
      </Dialog>
    </>
  );
}
